import { type AgentConnectionStatus, acpAgentCatalogEntryForId } from '@crowdex/agent-contracts';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import {
  type ConversationFailure,
  conversationFailure,
  failureReason,
} from '@/lib/conversations/precondition-failure';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import {
  createWorkspaceRecovery,
  unavailableConversationWorkspace,
} from '@/lib/desktop-shell/workspace-recovery';
import type { NativeAgent, NativeWorkspaceAction } from '@/lib/native-desktop';

type Recovery = Parameters<typeof createWorkspaceRecovery>[0];

/**
 * What keeps the open conversation from running, as its notices show it, with the way to fix it:
 * the chat's and the agent connection's failures and what is missing, an agent that is not
 * installed here, the terminal command that signs the agent in, and the recovery of a folder that
 * is not on this device (locate, clone, continue in Cloud). A turn refused because its folder is
 * gone marks the folder so, which shows that recovery.
 */
export function useConversationFailures(input: {
  store: DesktopShellStore;
  preview: boolean;
  agent: NativeAgent | null;
  cloudAgentSelected: boolean;
  messageCount: number;
  error: Error | undefined;
  connectionStatus: AgentConnectionStatus;
  runWorkspaceAction: (action: NativeWorkspaceAction) => void;
  selectWorkspace: () => Promise<unknown>;
  attach: Recovery['attach'];
  clone: Recovery['clone'];
  handOffToCloud: Recovery['handOffToCloud'];
}) {
  const { store, error, connectionStatus, agent } = input;
  const native = useStore(store, (state) => state.native);
  const discoveringAgents = useStore(store, (state) => state.discoveringAgents);
  const workspace = useStore(store, (state) => state.workspace);
  const chatId = useStore(store, (state) => state.chatId);
  const agents = useStore(store, (state) => state.agents);
  const syncedThreads = useStore(store, (state) => state.syncedThreads);
  const syncedWorkspaces = useStore(store, (state) => state.syncedWorkspaces);
  const [recoveryWorking, setRecoveryWorking] = useState(false);

  const chatError: ConversationFailure | null =
    error === undefined ? null : conversationFailure(error);
  const folderGone = chatError?.reason === 'folder_missing';
  useEffect(() => {
    if (!folderGone || workspace === null) return;
    store
      .getState()
      .setSyncedWorkspaces((current) =>
        current.some((entry) => entry.rootPath === workspace && entry.availableOnDevice !== false)
          ? current.map((entry) =>
              entry.rootPath === workspace ? { ...entry, availableOnDevice: false } : entry,
            )
          : current,
      );
  }, [folderGone, store, workspace]);

  const unavailable = unavailableConversationWorkspace(chatId, syncedThreads, syncedWorkspaces);
  const recovery = createWorkspaceRecovery({
    chatId,
    workspace: unavailable,
    agents,
    agent,
    setWorking: setRecoveryWorking,
    setError: store.getState().setWorkspaceProvisionError,
    attach: input.attach,
    clone: input.clone,
    handOffToCloud: input.handOffToCloud,
  });

  // A conversation whose agent is not installed here (nothing found, or uninstalled since).
  const agentMissing =
    !input.preview &&
    native === true &&
    !discoveringAgents &&
    agent === null &&
    !input.cloudAgentSelected &&
    input.messageCount > 0;
  const signIn =
    agent === null ? null : (acpAgentCatalogEntryForId(agent.id)?.signIn?.command ?? null);
  const connectionError: ConversationFailure | null =
    connectionStatus.state === 'error'
      ? {
          message: connectionStatus.message,
          reason: connectionStatus.reason ?? failureReason(connectionStatus.message),
        }
      : agentMissing
        ? {
            message:
              'No agent is installed on this device for this conversation. Install one, or choose another in Settings.',
            reason: 'no_agent',
          }
        : null;

  return {
    agentName: agent?.name ?? null,
    chatError,
    connectionError,
    onSignInAgent:
      native === true && signIn !== null
        ? () =>
            input.runWorkspaceAction({
              id: 'agent-sign-in',
              name: `Sign in to ${agent?.name ?? 'the agent'}`,
              icon: 'terminal',
              script: signIn,
            })
        : undefined,
    onSelectWorkspace: native === true ? () => void input.selectWorkspace() : undefined,
    onCloneWorkspace: native === true ? recovery.cloneLocally : undefined,
    onContinueInCloud: recovery.continueInCloud,
    onLocateWorkspace: native === true ? recovery.recoverLocally : undefined,
    workspaceRecovery:
      unavailable === null
        ? null
        : {
            name: unavailable.name,
            cloneable: native === true && recovery.cloneable,
            cloudAvailable: recovery.cloudAvailable,
          },
    workspaceRecoveryWorking: recoveryWorking,
  };
}
